import {
  Badge,
  Button,
  Card,
  Checkbox,
  Flex,
  Group,
  Loader,
  MultiSelect,
  NativeSelect,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Stepper,
  Text,
  Title,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import React, { useEffect, useState } from 'react';

import {
  IconAdjustmentsHorizontal,
  IconCalendar,
  IconChartBar,
  IconCirclePlus,
  IconFileInvoice,
  IconReportAnalytics,
} from '@tabler/icons-react';
import { DatePickerInput, MonthPickerInput } from '@mantine/dates';

import { useListTariffsByProvider } from 'api/resource';
import { useTranslation } from '@refinedev/core';
import { ReportModalProps } from 'interfaces';
import { ProviderSelect } from 'components/ProviderSelect';
import { useIsProvider } from 'hooks';
import { useCurrentUserStore } from 'store/UserStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { buttonTextColor } from 'utils/buttonUtils';
import { isFeatureEnabled } from 'firebase';
import {
  compareDateDesc,
  formatDateOnly,
  formatDateTimePattern,
  getCurrentMonthRange as getCurrentMonthDateRange,
  getCutoffRange,
  nowISODate,
  parseDateTime,
  toJSDate,
} from 'utils/dateUtils';
import { filterTariffOptions } from 'utils/tariffOptionFilter';
import { CustomReportModalContent } from './CustomReportModalContent';
import { fiscalYearEndMonthOptions } from 'utils/fiscalYear';

export const ReportModal = ({
  title,
  row,
  table,
  providers,
  remittances = [],
  internalEditComponents = [],
  initialType,
  setIsLoading,
}: ReportModalProps & {
  title: string;
  initialType?: string;
  disableChip?: { [key: string]: boolean };
  setIsLoading?: (isLoading: boolean) => void;
}) => {
  const theme = useMantineTheme();
  const { translate } = useTranslation();
  const [type, setType] = useState<string>(initialType || '');
  const colourMode = useComputedColorScheme('dark');
  const isProvider = useIsProvider();
  const { cognitoID } = useCurrentUserStore();
  const { id: siteID, timezone, province } = useCurrentSiteStore();
  const [activeStep, setActiveStep] = useState(0);
  const [hoveredCard, setHoveredCard] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);
  const [monthlyRevenueStatementMonth, setMonthlyRevenueStatementMonth] =
    useState<Date | null>(null);
  const [monthlyYear, setMonthlyYear] = useState<number>(
    parseDateTime(nowISODate(timezone)).year
  );
  const [providerReportDateType, setProviderReportDateType] = useState<
    'serviceDate' | 'paymentDate'
  >('serviceDate');
  const [exportType, setExportType] = useState<'pdf' | 'csv'>('pdf');
  const [accountsReceivableRange, setAccountsReceivableRange] = useState<
    [string | null, string | null]
  >([null, null]);
  const [accountsReceivableAllTime, setAccountsReceivableAllTime] =
    useState(false);
  const [tariffProviderRange, setTariffProviderRange] = useState<
    [string | null, string | null]
  >([null, null]);
  const [providerGroupingDateRange, setProviderGroupingDateRange] = useState<
    [string | null, string | null]
  >([null, null]);
  const [providerGroupingGroupIDs, setProviderGroupingGroupIDs] = useState<
    string[]
  >([]);
  const [groupingScope, setGroupingScope] = useState<'provider' | 'group'>(
    'provider'
  );
  const [monthlyRevenueStatementScope, setMonthlyRevenueStatementScope] =
    useState<'provider' | 'site'>('provider');
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [selectedGroupingRemittanceID, setSelectedGroupingRemittanceID] =
    useState<string>('');
  const [selectedProviderID, setSelectedProviderID] = useState<
    string | null | undefined
  >(
    (row._valuesCache?.providerID as string | undefined) ??
      (row.getValue?.('providerID') as string | undefined)
  );
  const [selectedRemittanceID, setSelectedRemittanceID] = useState<
    string | null | undefined
  >(
    (row._valuesCache?.remittanceID as string | undefined) ??
      (row.getValue?.('remittanceID') as string | undefined)
  );

  const [tariffProviderTariffs, setTariffProviderTariffs] = useState<string[]>(
    []
  );
  const [tariffSelectEnabled, setTariffSelectEnabled] = useState(false);
  const [fiscalYearEndMonth, setFiscalYearEndMonth] = useState<number>(12);
  const getCurrentYtdYear = (endMonth: number) => {
    const today = parseDateTime(nowISODate(timezone));
    return today.month <= endMonth ? today.year : today.year + 1;
  };
  const [ytdYear, setYtdYear] = useState<number>(() => getCurrentYtdYear(12));
  const CARD_ICON_SIZE = 34;
  const allTimeStart = '1900-01-01';
  const providerForUser = isProvider
    ? providers.find((provider) => provider.user?.cognitoID === cognitoID)
    : undefined;
  const providerOptions = isProvider
    ? providerForUser
      ? [providerForUser]
      : []
    : providers;
  const getProviderGroupsByID = (providerID?: string | null) => {
    if (!providerID) {
      return [];
    }
    const provider = providers.find((item) => item.id === providerID);
    return (provider?.groups ?? []).filter((group) => group.siteID === siteID);
  };
  const groupNameOptions = React.useMemo(() => {
    const namesByNormalizedValue = new Map<string, string>();
    providers.forEach((provider) => {
      (provider.groups ?? [])
        .filter((group) => group.siteID === siteID)
        .forEach((group) => {
          const normalizedName = group.name.toLowerCase();
          if (!namesByNormalizedValue.has(normalizedName)) {
            namesByNormalizedValue.set(normalizedName, group.name);
          }
        });
    });

    return Array.from(namesByNormalizedValue.values())
      .sort((left, right) =>
        left.localeCompare(right, undefined, { sensitivity: 'base' })
      )
      .map((name) => ({ value: name, label: name }));
  }, [providers, siteID]);
  const getCurrentMonthRange = () => {
    return getCurrentMonthDateRange(timezone);
  };
  const getMonthPickerDateTime = (value: Date | string) =>
    parseDateTime(value, { zone: timezone });
  const maxManualMonth = parseDateTime(nowISODate(timezone))
    .startOf('month')
    .minus({ days: 1 });
  const maxManualMonthDate = toJSDate(maxManualMonth);
  const remittanceOptions = [...remittances]
    .sort((left, right) => {
      return compareDateDesc(String(left.parsedAt), String(right.parsedAt));
    })
    .map((remittance) => {
      const formattedDate = formatDateTimePattern(
        String(remittance.parsedAt),
        'yyyy-MM-dd HH:mm',
        { timezone, fallback: 'Unknown date' }
      );

      return {
        value: remittance.id,
        label: `${formattedDate} - ${remittance.filename}`,
      };
    });

  const setGroupingRemittance = (remittanceID: string | null) => {
    const nextRemittanceID = remittanceID ?? '';
    setSelectedGroupingRemittanceID(nextRemittanceID);

    if (!nextRemittanceID) {
      delete row._valuesCache.remittanceID;
      row._valuesCache.startDate = null;
      row._valuesCache.endDate = null;
      return;
    }

    const remittance = remittances.find((item) => item.id === nextRemittanceID);
    if (!remittance) {
      delete row._valuesCache.remittanceID;
      row._valuesCache.startDate = null;
      row._valuesCache.endDate = null;
      return;
    }

    const parsedAt = parseDateTime(String(remittance.parsedAt), {
      zone: timezone,
    });
    if (!parsedAt.isValid) {
      delete row._valuesCache.remittanceID;
      row._valuesCache.startDate = null;
      row._valuesCache.endDate = null;
      return;
    }

    const cutoffRange = getCutoffRange(parsedAt, undefined, province);
    row._valuesCache.remittanceID = remittance.id;
    row._valuesCache.startDate = cutoffRange.start.toISODate();
    row._valuesCache.endDate = cutoffRange.end.toISODate();
  };

  const closeModal = () => {
    table.setCreatingRow(null);
    table.setEditingRow(null);
  };

  const reportYearOptions = Array.from({ length: 11 }, (_, index) => {
    const year = parseDateTime(nowISODate(timezone)).year + 1 - index;
    return { value: String(year), label: String(year) };
  });

  const updateYtdRange = (endMonth: number, year: number) => {
    const endDate = parseDateTime(
      `${year}-${String(endMonth).padStart(2, '0')}-01`,
      { zone: timezone }
    )
      .endOf('month')
      .startOf('day');
    const startDate = endDate.minus({ years: 1 });

    row._valuesCache.startDate = startDate.toISODate();
    row._valuesCache.endDate = endDate.toISODate();
  };

  useEffect(() => {
    if (type === 'ytd') {
      updateYtdRange(fiscalYearEndMonth, ytdYear);
    }
  }, [type, fiscalYearEndMonth, ytdYear, row]);

  useEffect(() => {
    if (type === 'monthlyRevenueStatement') {
      row._valuesCache.dateType = 'serviceDate';
      return;
    }

    if (type === 'monthly' || type === 'ytd' || type === 'grouping') {
      row._valuesCache.dateType = providerReportDateType;
    }
  }, [providerReportDateType, row, type]);

  useEffect(() => {
    if (providerForUser?.id && selectedProviderID !== providerForUser.id) {
      setSelectedProviderID(providerForUser.id);
      row._valuesCache.providerID = providerForUser.id;
      if (type === 'grouping') {
        const groupIDs = getProviderGroupsByID(providerForUser.id).map(
          (group) => group.id
        );
        setProviderGroupingGroupIDs(groupIDs);
        row._valuesCache.groupIDs = groupIDs;
      }
    }
  }, [providerForUser?.id, row, selectedProviderID, type]);

  const onSave = () => {
    delete row._valuesCache.year;
    row._valuesCache.subtype = type;
    if (type === 'monthlyRevenueStatement') {
      row._valuesCache.reportScope = monthlyRevenueStatementScope;
      if (monthlyRevenueStatementScope === 'site') {
        delete row._valuesCache.providerID;
      }
    }
    if (type !== 'tariffProviderReport' && type !== 'statementIncome') {
      row._valuesCache.exportType = exportType;
    } else {
      delete row._valuesCache.exportType;
    }
    table.options.onCreatingRowSave?.({
      exitCreatingMode: () => table.setCreatingRow(null),
      row,
      table,
      values: row._valuesCache,
    });
  };

  const enhanceInternalComponent = (component: React.ReactElement) => {
    const key = component.key?.toString() ?? '';
    const element = component as React.ReactElement<any>;

    if (key.includes('providerID')) {
      const existingOnChange = element.props?.onChange as
        | ((value: string | null, provider?: unknown) => void)
        | undefined;
      return React.cloneElement(element, {
        onChange: (value: string | null, provider?: unknown) => {
          existingOnChange?.(value, provider);
          row._valuesCache.providerID = value ?? null;
          setSelectedProviderID(value ?? null);
        },
      });
    }

    return component;
  };

  const matchInputs = (keys: string[]) =>
    internalEditComponents
      .filter((component) => {
        const key = (component as React.ReactElement).key?.toString();
        return Boolean(key && keys.some((value) => key.includes(value)));
      })
      .map((component) =>
        enhanceInternalComponent(component as React.ReactElement)
      );

  const renderConditionalInput = () => {
    switch (type) {
      case 'monthly':
        return [
          ...matchInputs(['providerID']),
          <Select
            key="provider-report-date-type"
            label="Date Type"
            placeholder="Select date type"
            data={[
              { value: 'serviceDate', label: 'Service Date' },
              { value: 'paymentDate', label: 'Payment Date' },
            ]}
            value={providerReportDateType}
            onChange={(value) => {
              const next =
                value === 'paymentDate' ? 'paymentDate' : 'serviceDate';
              setProviderReportDateType(next);
              row._valuesCache.dateType = next;
            }}
            radius="lg"
            w="60%"
          />,
          <Select
            key="provider-report-year-monthly"
            label="Report Year"
            placeholder="Select year"
            data={reportYearOptions}
            value={String(monthlyYear)}
            onChange={(value) => {
              if (!value) {
                return;
              }
              const nextYear = Number(value);
              setMonthlyYear(nextYear);
              const baseDate = selectedMonth
                ? parseDateTime(selectedMonth, { zone: timezone })
                : parseDateTime(nowISODate(timezone));
              const updated = baseDate.set({ year: nextYear }).startOf('month');
              setSelectedMonth(toJSDate(updated));
              row._valuesCache.startDate = updated.startOf('month').toISODate();
              row._valuesCache.endDate = updated.endOf('month').toISODate();
            }}
            radius="lg"
            w="60%"
          />,
          <MonthPickerInput
            key="report-month"
            label="Report Month"
            placeholder="Pick a month"
            value={selectedMonth}
            onChange={(value) => {
              if (!value) {
                setSelectedMonth(null);
                return;
              }
              const selectedDateTime = parseDateTime(value, {
                zone: timezone,
              });
              setSelectedMonth(toJSDate(selectedDateTime));
              setMonthlyYear(selectedDateTime.year);

              const start = selectedDateTime.startOf('month');
              const end = selectedDateTime.endOf('month');
              row._valuesCache.startDate = start.toISODate();
              row._valuesCache.endDate = end.toISODate();
            }}
            radius="lg"
            w="60%"
            clearable
          />,
        ];
      case 'ytd':
        return [
          ...matchInputs(['providerID']),
          <Select
            key="provider-report-date-type"
            label="Date Type"
            placeholder="Select date type"
            data={[
              { value: 'serviceDate', label: 'Service Date' },
              { value: 'paymentDate', label: 'Payment Date' },
            ]}
            value={providerReportDateType}
            onChange={(value) => {
              const next =
                value === 'paymentDate' ? 'paymentDate' : 'serviceDate';
              setProviderReportDateType(next);
              row._valuesCache.dateType = next;
            }}
            radius="lg"
            w="60%"
          />,
          <Select
            key="provider-report-year"
            label="Report Year"
            placeholder="Select year"
            data={reportYearOptions}
            value={String(ytdYear)}
            onChange={(value) => {
              if (!value) {
                return;
              }
              const nextYear = Number(value);
              setYtdYear(nextYear);
            }}
            radius="lg"
            w="60%"
          />,
          <Select
            key="fiscal-year-end"
            label="Fiscal Year End Month"
            placeholder="Select fiscal year end"
            data={fiscalYearEndMonthOptions}
            value={String(fiscalYearEndMonth)}
            onChange={(value) => {
              if (value) {
                const month = parseInt(value, 10);
                setFiscalYearEndMonth(month);
              }
            }}
            radius="lg"
            w="60%"
          />,
        ];
      case 'monthlyRevenueStatement': {
        const providerID =
          selectedProviderID !== undefined
            ? selectedProviderID ?? undefined
            : (row._valuesCache?.providerID as string | undefined) ??
              (row.getValue?.('providerID') as string | undefined);

        return [
          ...(!isProvider
            ? [
                <SegmentedControl
                  key="monthly-revenue-statement-scope"
                  aria-label={translate(
                    'pages:reports.monthlyRevenueStatement.scope.label',
                    'Report Scope'
                  )}
                  value={monthlyRevenueStatementScope}
                  onChange={(value) => {
                    const nextScope = value === 'site' ? 'site' : 'provider';
                    setMonthlyRevenueStatementScope(nextScope);
                    row._valuesCache.reportScope = nextScope;

                    if (nextScope === 'site') {
                      delete row._valuesCache.providerID;
                      return;
                    }

                    if (selectedProviderID) {
                      row._valuesCache.providerID = selectedProviderID;
                    }
                  }}
                  data={[
                    {
                      value: 'provider',
                      label: translate(
                        'pages:reports.monthlyRevenueStatement.scope.provider',
                        'Provider'
                      ),
                    },
                    {
                      value: 'site',
                      label: translate(
                        'pages:reports.monthlyRevenueStatement.scope.site',
                        'Site'
                      ),
                    },
                  ]}
                />,
              ]
            : []),
          ...(monthlyRevenueStatementScope === 'provider'
            ? [
                <ProviderSelect
                  key="monthly-revenue-provider-select"
                  providers={providerOptions}
                  value={providerID ?? null}
                  onChange={(value) => {
                    row._valuesCache.providerID = value ?? null;
                    setSelectedProviderID(value ?? null);
                  }}
                  radius="lg"
                  w="60%"
                  disabled={isProvider}
                />,
              ]
            : []),
          <MonthPickerInput
            key="monthly-revenue-statement-month"
            label={translate(
              'pages:reports.monthlyRevenueStatement.month.label',
              'Statement Month'
            )}
            placeholder={translate(
              'pages:reports.monthlyRevenueStatement.month.placeholder',
              'Pick a month'
            )}
            value={monthlyRevenueStatementMonth}
            onChange={(value) => {
              if (!value) {
                setMonthlyRevenueStatementMonth(null);
                row._valuesCache.startDate = null;
                row._valuesCache.endDate = null;
                return;
              }

              const selectedDateTime = getMonthPickerDateTime(value);
              if (!selectedDateTime.isValid) {
                return;
              }

              const selectedMonth = selectedDateTime.startOf('month');
              if (
                selectedMonth.toMillis() >
                maxManualMonth.startOf('month').toMillis()
              ) {
                return;
              }

              setMonthlyRevenueStatementMonth(selectedMonth.toJSDate());
              row._valuesCache.startDate = selectedMonth
                .startOf('month')
                .toISODate();
              row._valuesCache.endDate = selectedMonth
                .endOf('month')
                .toISODate();
              row._valuesCache.dateType = 'serviceDate';
            }}
            radius="lg"
            w="60%"
            clearable
            maxDate={maxManualMonthDate}
          />,
        ];
      }
      case 'grouping': {
        const scopeControl = !isProvider ? (
          <SegmentedControl
            key="grouping-scope"
            aria-label={translate(
              'pages:reports.groupIncome.scope.label',
              'Report Scope'
            )}
            value={groupingScope}
            onChange={(value) => {
              const nextScope = value === 'group' ? 'group' : 'provider';
              setGroupingScope(nextScope);
              row._valuesCache.reportScope = nextScope;
              setGroupingRemittance(null);

              if (nextScope === 'group') {
                setSelectedProviderID(null);
                setProviderGroupingGroupIDs([]);
                delete row._valuesCache.providerID;
                delete row._valuesCache.groupIDs;
                delete row._valuesCache.dateType;
                delete row._valuesCache.startDate;
                delete row._valuesCache.endDate;
                return;
              }

              setSelectedGroupName('');
              delete row._valuesCache.groupName;
              setProviderReportDateType('serviceDate');
              row._valuesCache.dateType = 'serviceDate';
              const nextRange = getCurrentMonthRange();
              setProviderGroupingDateRange(nextRange);
              row._valuesCache.startDate = nextRange[0];
              row._valuesCache.endDate = nextRange[1];
            }}
            data={[
              {
                value: 'provider',
                label: translate(
                  'pages:reports.groupIncome.scope.provider',
                  'Provider'
                ),
              },
              {
                value: 'group',
                label: translate(
                  'pages:reports.groupIncome.scope.group',
                  'Group'
                ),
              },
            ]}
            radius="lg"
          />
        ) : null;

        if (groupingScope === 'group' && !isProvider) {
          return [
            scopeControl,
            <Select
              key="group-income-group"
              label={translate(
                'pages:reports.groupIncome.group.label',
                'Group'
              )}
              placeholder={translate(
                'pages:reports.groupIncome.group.placeholder',
                'Select a group'
              )}
              data={groupNameOptions}
              value={selectedGroupName || null}
              onChange={(value) => {
                const nextGroupName = value ?? '';
                setSelectedGroupName(nextGroupName);
                row._valuesCache.groupName = nextGroupName || undefined;
              }}
              searchable
              radius="lg"
              w="60%"
            />,
            <NativeSelect
              key="group-income-remittance"
              label={translate(
                'pages:reports.groupIncome.remittance.label',
                'Remittance Intake Date'
              )}
              aria-label={translate(
                'pages:reports.groupIncome.remittance.label',
                'Remittance Intake Date'
              )}
              data={[
                {
                  value: '',
                  label: translate(
                    'pages:reports.groupIncome.remittance.placeholder',
                    'Select remittance'
                  ),
                },
                ...remittanceOptions,
              ]}
              value={selectedGroupingRemittanceID}
              onChange={(event) => {
                const remittanceID = event.currentTarget.value;
                setSelectedGroupingRemittanceID(remittanceID);
                row._valuesCache.remittanceID = remittanceID || undefined;
              }}
              radius="lg"
              w="60%"
            />,
          ];
        }

        const providerID =
          selectedProviderID !== undefined
            ? selectedProviderID ?? undefined
            : (row._valuesCache?.providerID as string | undefined) ??
              (row.getValue?.('providerID') as string | undefined);
        const providerGroupOptions = getProviderGroupsByID(providerID)
          .map((group) => ({
            value: group.id,
            label: group.name,
          }))
          .sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true })
          );

        return [
          scopeControl,
          <ProviderSelect
            key="provider-grouping-provider-select"
            providers={providerOptions}
            value={providerID ?? null}
            onChange={(value) => {
              row._valuesCache.providerID = value ?? null;
              setSelectedProviderID(value ?? null);
              const groupIDs = getProviderGroupsByID(value).map(
                (group) => group.id
              );
              setProviderGroupingGroupIDs(groupIDs);
              row._valuesCache.groupIDs = groupIDs;
            }}
            radius="lg"
            w="60%"
            disabled={isProvider}
          />,
          <Select
            key="provider-grouping-date-type"
            label="Date Type"
            placeholder="Select date type"
            data={[
              { value: 'serviceDate', label: 'Service Date' },
              { value: 'paymentDate', label: 'Remittance Date' },
            ]}
            value={providerReportDateType}
            onChange={(value) => {
              const next =
                value === 'paymentDate' ? 'paymentDate' : 'serviceDate';
              setProviderReportDateType(next);
              row._valuesCache.dateType = next;
              if (next === 'paymentDate') {
                setProviderGroupingDateRange([null, null]);
                row._valuesCache.startDate = null;
                row._valuesCache.endDate = null;
              } else {
                setGroupingRemittance(null);
                const nextRange = getCurrentMonthRange();
                setProviderGroupingDateRange(nextRange);
                row._valuesCache.startDate = nextRange[0];
                row._valuesCache.endDate = nextRange[1];
              }
            }}
            radius="lg"
            w="60%"
          />,
          ...(providerReportDateType === 'paymentDate'
            ? [
                <NativeSelect
                  key="provider-grouping-remittance"
                  label="Remittance Intake Date"
                  aria-label="Remittance Intake Date"
                  data={[
                    { value: '', label: 'Select remittance' },
                    ...remittanceOptions,
                  ]}
                  value={selectedGroupingRemittanceID}
                  onChange={(event) => {
                    setGroupingRemittance(event.currentTarget.value || null);
                  }}
                  radius="lg"
                  w="60%"
                />,
              ]
            : [
                <DatePickerInput
                  key="provider-grouping-date-range"
                  type="range"
                  label="Date range"
                  placeholder="Date range"
                  value={providerGroupingDateRange}
                  onChange={(dates) => {
                    const next = dates as [string | null, string | null];
                    setProviderGroupingDateRange(next);
                    row._valuesCache.startDate = next[0];
                    row._valuesCache.endDate = next[1];
                  }}
                  radius="lg"
                  w="60%"
                />,
              ]),
          ...(providerGroupOptions.length > 0
            ? [
                <MultiSelect
                  key="provider-grouping-groups"
                  label="Provider Groups"
                  placeholder="Select provider groups"
                  data={providerGroupOptions}
                  value={providerGroupingGroupIDs}
                  onChange={(values) => {
                    setProviderGroupingGroupIDs(values);
                    row._valuesCache.groupIDs = values;
                  }}
                  searchable
                  radius="lg"
                  w="60%"
                />,
              ]
            : []),
        ];
      }
      case 'accountsReceivable':
        return [
          ...matchInputs(['providerID']),
          <Stack key="accounts-receivable-date-range" gap="xs" w="60%">
            <DatePickerInput
              type="range"
              label="Date range"
              placeholder="Date range"
              value={accountsReceivableRange}
              onChange={(dates) => {
                const next = dates as [string | null, string | null];
                setAccountsReceivableRange(next);
                if (!accountsReceivableAllTime) {
                  row._valuesCache.startDate = next[0];
                  row._valuesCache.endDate = next[1];
                }
              }}
              radius="lg"
              w="100%"
              disabled={accountsReceivableAllTime}
            />
            <Checkbox
              label="All time (outstanding)"
              checked={accountsReceivableAllTime}
              onChange={(event) => {
                const isChecked = event.currentTarget.checked;
                setAccountsReceivableAllTime(isChecked);
                if (isChecked) {
                  const nextRange: [string, string] = [
                    allTimeStart,
                    nowISODate(timezone) || allTimeStart,
                  ];
                  setAccountsReceivableRange(nextRange);
                  row._valuesCache.startDate = nextRange[0];
                  row._valuesCache.endDate = nextRange[1];
                } else {
                  const nextRange = getCurrentMonthRange();
                  setAccountsReceivableRange(nextRange);
                  row._valuesCache.startDate = nextRange[0];
                  row._valuesCache.endDate = nextRange[1];
                }
              }}
            />
          </Stack>,
        ];
      case 'tariffProviderReport': {
        const providerID =
          selectedProviderID !== undefined
            ? selectedProviderID ?? undefined
            : (row._valuesCache?.providerID as string | undefined) ??
              (row.getValue?.('providerID') as string | undefined);
        return [
          <ProviderSelect
            key="tariff-provider-select"
            providers={providerOptions}
            value={providerID ?? null}
            onChange={(value) => {
              row._valuesCache.providerID = value ?? null;
              setSelectedProviderID(value ?? null);
            }}
            radius="lg"
            w="60%"
            disabled={isProvider}
          />,
          <Stack key="tariff-provider-options" gap="xs" w="60%">
            <DatePickerInput
              type="range"
              label="Date range"
              placeholder="Date range"
              value={tariffProviderRange}
              onChange={(dates) => {
                const next = dates as [string | null, string | null];
                setTariffProviderRange(next);
                row._valuesCache.startDate = next[0];
                row._valuesCache.endDate = next[1];
              }}
              radius="lg"
              w="100%"
            />
            <Checkbox
              label="Select specific tariffs"
              checked={tariffSelectEnabled}
              onChange={(event) => {
                const isChecked = event.currentTarget.checked;
                setTariffSelectEnabled(isChecked);
                if (!isChecked) {
                  setTariffProviderTariffs([]);
                  row._valuesCache.tariffIDs = [];
                }
              }}
            />
            <TariffMultiSelect
              value={tariffProviderTariffs}
              providerID={providerID}
              siteID={siteID}
              onChange={(values) => {
                setTariffProviderTariffs(values);
                row._valuesCache.tariffIDs = values;
              }}
              enabled={tariffSelectEnabled}
            />
          </Stack>,
        ];
      }
      case 'statementIncome': {
        const providerID =
          selectedProviderID !== undefined
            ? selectedProviderID ?? undefined
            : (row._valuesCache?.providerID as string | undefined) ??
              (row.getValue?.('providerID') as string | undefined);
        const remittanceOptions = remittances
          .slice()
          .sort((a, b) => compareDateDesc(a.parsedAt, b.parsedAt))
          .map((remittance) => {
            const parsedAt = parseDateTime(remittance.parsedAt);
            const label = parsedAt.isValid
              ? formatDateOnly(parsedAt)
              : remittance.filename;
            return {
              value: remittance.id,
              label,
            };
          });

        return [
          <ProviderSelect
            key="statement-income-provider-select"
            providers={providerOptions}
            value={providerID ?? null}
            onChange={(value) => {
              row._valuesCache.providerID = value ?? null;
              setSelectedProviderID(value ?? null);
            }}
            radius="lg"
            w="60%"
            disabled={isProvider}
          />,
          <Select
            key="statement-income-remittance-select"
            label="Remittance Date"
            placeholder="Select remittance"
            data={remittanceOptions}
            value={selectedRemittanceID ?? null}
            onChange={(value) => {
              row._valuesCache.remittanceID = value ?? null;
              setSelectedRemittanceID(value ?? null);
            }}
            radius="lg"
            w="60%"
            searchable
          />,
        ];
      }
      default:
        return [];
    }
  };

  const shouldShowExportType =
    type !== '' &&
    type !== 'tariffProviderReport' &&
    type !== 'customReport' &&
    type !== 'statementIncome';
  const isCustomReportFlow = type === 'customReport';

  const lastStep = 1;
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === lastStep;
  const groupingProviderID =
    selectedProviderID !== undefined
      ? selectedProviderID ?? undefined
      : (row._valuesCache?.providerID as string | undefined) ??
        (row.getValue?.('providerID') as string | undefined);
  const hasMonthlyRevenueMonthRange =
    !!row._valuesCache.startDate && !!row._valuesCache.endDate;
  const groupingProviderGroups = getProviderGroupsByID(groupingProviderID);
  const isTariffProviderInvalid =
    type === 'tariffProviderReport' &&
    (!tariffProviderRange[0] || !tariffProviderRange[1]);
  const isProviderGroupingInvalid =
    type === 'grouping' &&
    (groupingScope === 'group' && !isProvider
      ? !selectedGroupName || !selectedGroupingRemittanceID
      : !selectedProviderID ||
        (providerReportDateType === 'paymentDate'
          ? !selectedGroupingRemittanceID
          : !providerGroupingDateRange[0] || !providerGroupingDateRange[1]) ||
        (groupingProviderGroups.length > 0 &&
          providerGroupingGroupIDs.length === 0));
  const isMonthlyRevenueStatementInvalid =
    type === 'monthlyRevenueStatement' &&
    (!hasMonthlyRevenueMonthRange ||
      (monthlyRevenueStatementScope === 'provider' && !selectedProviderID));
  const isStatementIncomeInvalid =
    type === 'statementIncome' &&
    (!selectedProviderID || !selectedRemittanceID);
  const isActionDisabled =
    (isFirstStep && !type) ||
    (isLastStep &&
      (isTariffProviderInvalid ||
        isProviderGroupingInvalid ||
        isMonthlyRevenueStatementInvalid ||
        isStatementIncomeInvalid));

  const REPORT_OPTIONS = [
    {
      value: 'statementIncome',
      label: 'Statement Income',
      description:
        'Generate a statement-style income report for a provider and remittance.',
      guidance: 'Select a provider and remittance date.',
      enabled: !isProvider && isFeatureEnabled('report_income'),
      icon: (
        <IconFileInvoice
          style={{ color: theme.colors[`primary-${colourMode}`][0] }}
          size={CARD_ICON_SIZE}
        />
      ),
    },
    {
      value: 'monthlyRevenueStatement',
      label: 'Monthly Revenue Statement',
      description: 'Monthly revenue totals with year-to-date summary.',
      guidance: translate(
        'pages:reports.monthlyRevenueStatement.guidance',
        'Select the report scope and statement month.'
      ),
      enabled: isFeatureEnabled('report_monthly_revenue_statement'),
      icon: (
        <IconCalendar
          style={{ color: theme.colors[`primary-${colourMode}`][0] }}
          size={CARD_ICON_SIZE}
        />
      ),
    },
    {
      value: 'grouping',
      label: 'Grouping Report',
      description: 'Summarize claims by groups',
      guidance: 'Select grouping criteria and date range.',
      enabled: isFeatureEnabled('report_provider_grouping'),
      icon: (
        <IconReportAnalytics
          style={{ color: theme.colors[`primary-${colourMode}`][0] }}
          size={CARD_ICON_SIZE}
        />
      ),
    },
    {
      value: 'monthly',
      label: 'Provider Monthly',
      description: 'Monthly summary of activity for a single provider.',
      guidance: 'Select a provider and a month.',
      enabled: isFeatureEnabled('report_provider_monthly'),
      icon: (
        <IconCalendar
          style={{ color: theme.colors[`primary-${colourMode}`][0] }}
          size={CARD_ICON_SIZE}
        />
      ),
    },
    {
      value: 'ytd',
      label: 'Provider YTD',
      description: 'Year-to-date view of provider activity.',
      guidance: 'Select a provider and the YTD date range.',
      enabled: isFeatureEnabled('report_provider_ytd'),
      icon: (
        <IconChartBar
          style={{ color: theme.colors[`primary-${colourMode}`][0] }}
          size={CARD_ICON_SIZE}
        />
      ),
    },
    {
      value: 'accountsReceivable',
      label: 'Accounts Receivable',
      description: 'Outstanding balances and payment status by provider.',
      guidance: 'Select a provider and date range.',
      enabled: isFeatureEnabled('report_accounts_receivable'),
      icon: (
        <IconFileInvoice
          style={{ color: theme.colors[`primary-${colourMode}`][0] }}
          size={CARD_ICON_SIZE}
        />
      ),
    },
    {
      value: 'tariffProviderReport',
      label: 'Tariff Usage',
      description: 'Summarize tariff usage and payments for a provider.',
      guidance: 'Select a provider, date range, and tariffs.',
      enabled: isFeatureEnabled('report_tariff_provider'),
      icon: (
        <IconReportAnalytics
          style={{ color: theme.colors[`primary-${colourMode}`][0] }}
          size={CARD_ICON_SIZE}
        />
      ),
    },
    {
      value: 'customReport',
      label: 'Custom Report',
      description: 'Create custom reports with flexible data selection.',
      guidance: 'Choose filters and columns for your export.',
      enabled: !isProvider && isFeatureEnabled('report_custom'),
      icon: (
        <IconAdjustmentsHorizontal
          style={{ color: theme.colors[`primary-${colourMode}`][0] }}
          size={CARD_ICON_SIZE}
        />
      ),
    },
  ];

  const enabledReportOptions = REPORT_OPTIONS.filter(
    (option) => option.enabled
  );

  useEffect(() => {
    if (type && !enabledReportOptions.some((option) => option.value === type)) {
      setType('');
    }
  }, [enabledReportOptions, type]);

  const activeReport = enabledReportOptions.find(
    (option) => option.value === type
  );

  const handleCancelOrPrev = () => {
    if (isFirstStep) {
      table.setCreatingRow(null);
    } else {
      setActiveStep((s) => s - 1);
    }
  };

  const handleNextOrSave = () => {
    if (!type && isFirstStep) {
      return;
    }

    if (isLastStep) {
      onSave();
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  return (
    <Stack
      data-testid="create-report-modal"
      style={{ marginLeft: '5%', marginRight: '5%' }}
    >
      <Title order={3}>{title}</Title>
      {isCustomReportFlow && isLastStep ? (
        <CustomReportModalContent
          setIsLoading={setIsLoading ?? (() => {})}
          close={closeModal}
        />
      ) : (
        <Stepper active={activeStep} onStepClick={setActiveStep}>
          <Stepper.Step label="Report Type">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
              {enabledReportOptions.map((option) => {
                const isActive = type === option.value;
                const isHovered = hoveredCard === option.value;

                return (
                  <Card
                    key={option.value}
                    padding="lg"
                    radius="lg"
                    data-testid={'report-type-card'}
                    withBorder
                    onMouseEnter={() => setHoveredCard(option.value)}
                    onMouseLeave={() => setHoveredCard('')}
                    onClick={() => {
                      const existingRemittanceID =
                        (row._valuesCache?.remittanceID as
                          | string
                          | undefined) ??
                        (row.getValue?.('remittanceID') as string | undefined);
                      row._valuesCache.subtype = option.value;
                      setSelectedMonth(null);
                      setMonthlyRevenueStatementMonth(null);
                      setSelectedGroupingRemittanceID('');
                      setType(option.value);
                      if (
                        option.value !== 'monthlyRevenueStatement' &&
                        selectedProviderID &&
                        !row._valuesCache.providerID
                      ) {
                        row._valuesCache.providerID = selectedProviderID;
                      }
                      if (option.value !== 'grouping') {
                        setGroupingScope('provider');
                        setSelectedGroupName('');
                        delete row._valuesCache.reportScope;
                        delete row._valuesCache.groupName;
                      }
                      if (
                        option.value !== 'tariffProviderReport' &&
                        option.value !== 'customReport'
                      ) {
                        setExportType('pdf');
                        row._valuesCache.exportType = 'pdf';
                      } else {
                        delete row._valuesCache.exportType;
                      }
                      if (option.value === 'accountsReceivable') {
                        const nextRange: [string, string] = [
                          allTimeStart,
                          nowISODate(timezone) || allTimeStart,
                        ];
                        setAccountsReceivableAllTime(true);
                        setAccountsReceivableRange(nextRange);
                        row._valuesCache.startDate = nextRange[0];
                        row._valuesCache.endDate = nextRange[1];
                      } else if (option.value === 'tariffProviderReport') {
                        const nextRange = getCurrentMonthRange();
                        setTariffProviderRange(nextRange);
                        setTariffProviderTariffs([]);
                        setTariffSelectEnabled(false);
                        row._valuesCache.startDate = nextRange[0];
                        row._valuesCache.endDate = nextRange[1];
                        row._valuesCache.tariffIDs = [];
                        setProviderGroupingGroupIDs([]);
                        row._valuesCache.groupIDs = [];
                      } else if (
                        option.value === 'monthly' ||
                        option.value === 'ytd'
                      ) {
                        setProviderGroupingGroupIDs([]);
                        row._valuesCache.groupIDs = [];
                        setProviderReportDateType('serviceDate');
                        row._valuesCache.dateType = 'serviceDate';
                        if (option.value === 'ytd') {
                          const defaultYear =
                            getCurrentYtdYear(fiscalYearEndMonth);
                          setYtdYear(defaultYear);
                        } else if (option.value === 'monthly') {
                          setMonthlyYear(
                            parseDateTime(nowISODate(timezone)).year
                          );
                        }
                      } else if (option.value === 'monthlyRevenueStatement') {
                        setMonthlyRevenueStatementScope('provider');
                        row._valuesCache.reportScope = 'provider';
                        setProviderGroupingGroupIDs([]);
                        row._valuesCache.groupIDs = [];
                        setProviderReportDateType('serviceDate');
                        row._valuesCache.dateType = 'serviceDate';
                        const defaultMonth = parseDateTime(nowISODate(timezone))
                          .minus({ months: 1 })
                          .startOf('month');
                        setMonthlyRevenueStatementMonth(toJSDate(defaultMonth));
                        row._valuesCache.startDate = defaultMonth
                          .startOf('month')
                          .toISODate();
                        row._valuesCache.endDate = defaultMonth
                          .endOf('month')
                          .toISODate();
                      } else if (option.value === 'grouping') {
                        setGroupingScope('provider');
                        row._valuesCache.reportScope = 'provider';
                        setSelectedGroupName('');
                        delete row._valuesCache.groupName;
                        setProviderReportDateType('serviceDate');
                        row._valuesCache.dateType = 'serviceDate';
                        setSelectedGroupingRemittanceID('');
                        delete row._valuesCache.remittanceID;
                        const nextRange = getCurrentMonthRange();
                        setProviderGroupingDateRange(nextRange);
                        row._valuesCache.startDate = nextRange[0];
                        row._valuesCache.endDate = nextRange[1];
                        const groupIDs = getProviderGroupsByID(
                          selectedProviderID ?? null
                        ).map((group) => group.id);
                        setProviderGroupingGroupIDs(groupIDs);
                        row._valuesCache.groupIDs = groupIDs;
                      } else if (option.value === 'statementIncome') {
                        setSelectedRemittanceID(existingRemittanceID ?? null);
                        if (existingRemittanceID) {
                          row._valuesCache.remittanceID = existingRemittanceID;
                        } else {
                          delete row._valuesCache.remittanceID;
                        }
                      } else {
                        setAccountsReceivableAllTime(false);
                        setTariffProviderTariffs([]);
                        setTariffSelectEnabled(false);
                        setProviderGroupingGroupIDs([]);
                        setSelectedRemittanceID(null);
                        delete row._valuesCache.remittanceID;
                        row._valuesCache.tariffIDs = [];
                        row._valuesCache.groupIDs = [];
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                      transition:
                        'border-color 0.2s ease-in-out, transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      transform:
                        isActive || isHovered ? 'translateY(-6px)' : 'none',
                      boxShadow:
                        isActive || isHovered
                          ? '0px 12px 24px rgba(0, 0, 0, 0.3)'
                          : '0px 6px 16px rgba(0, 0, 0, 0.2)',
                      border: `2px solid ${
                        isActive || isHovered
                          ? theme.colors[`primary-${colourMode}`][0]
                          : theme.colors[`gray-${colourMode}`][0]
                      }`,
                      background:
                        colourMode === 'dark'
                          ? theme.colors['gray-light'][0]
                          : '#ffffff',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Stack
                      gap={6}
                      style={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Group
                        gap="sm"
                        wrap="nowrap"
                        align="flex-start"
                        style={{ width: '100%' }}
                      >
                        {option.icon}
                        <Text
                          fw={700}
                          size="lg"
                          style={{
                            flex: 1,
                            minWidth: 0,
                            whiteSpace: 'normal',
                          }}
                        >
                          {option.label}
                        </Text>
                      </Group>
                      <Text size="sm" c={theme.colors[`gray-${colourMode}`][0]}>
                        {option.description}
                      </Text>
                    </Stack>
                    <Text mt="md" mb="md" size="sm" fw={600}>
                      {option.guidance}
                    </Text>
                    {isActive && (
                      <Flex justify="center">
                        <Badge variant="filled" color="blue">
                          Selected
                        </Badge>
                      </Flex>
                    )}
                  </Card>
                );
              })}
            </SimpleGrid>
          </Stepper.Step>
          <Stepper.Step label="Details">
            <Stack align="center" gap="lg">
              <Card
                padding="lg"
                radius="lg"
                withBorder
                style={{
                  width: '60%',
                  background:
                    colourMode === 'dark'
                      ? theme.colors['gray-light'][0]
                      : '#ffffff',
                }}
              >
                <Group justify="space-between">
                  <Stack gap={4}>
                    <Text size="sm" c={theme.colors[`gray-${colourMode}`][0]}>
                      Selected report
                    </Text>
                    <Text fw={700} size="lg">
                      {activeReport?.label || 'Choose a report type'}
                    </Text>
                    <Text size="sm" c={theme.colors[`gray-${colourMode}`][0]}>
                      {activeReport?.description ||
                        'Go back and pick a report type to continue.'}
                    </Text>
                  </Stack>
                  {activeReport?.icon}
                </Group>
              </Card>
              <Flex
                justify="center"
                direction="column"
                align="center"
                gap="lg"
                style={{ width: '100%' }}
              >
                {renderConditionalInput()}
                {shouldShowExportType && (
                  <Card withBorder radius="md" p="md" w="60%">
                    <Stack gap="xs">
                      <Text fw={600}>Export Format</Text>
                      <SegmentedControl
                        value={exportType}
                        onChange={(val) => {
                          const next = val === 'csv' ? 'csv' : 'pdf';
                          setExportType(next);
                          row._valuesCache.exportType = next;
                        }}
                        data={[
                          { label: 'PDF', value: 'pdf' },
                          { label: 'CSV', value: 'csv' },
                        ]}
                      />
                    </Stack>
                  </Card>
                )}
                {internalEditComponents.map((component) => {
                  const key = (component as React.ReactElement).key?.toString();
                  if (key?.includes('userID') || key?.includes('siteID')) {
                    return enhanceInternalComponent(
                      component as React.ReactElement
                    );
                  }
                  return null;
                })}
              </Flex>
            </Stack>
          </Stepper.Step>
        </Stepper>
      )}
      {!(isCustomReportFlow && isLastStep) && (
        <Flex justify={'center'} gap={'lg'} style={{ marginTop: '1%' }}>
          <Button
            variant="outline"
            radius="md"
            color={isFirstStep ? 'gray' : null}
            c={buttonTextColor(colourMode, false)}
            onClick={handleCancelOrPrev}
          >
            {isFirstStep ? 'Cancel' : 'Previous'}
          </Button>

          <Button
            type={isLastStep ? 'submit' : 'button'}
            variant="outline"
            radius="md"
            color={isLastStep ? 'green' : null}
            c={buttonTextColor(colourMode, false)}
            onClick={handleNextOrSave}
            disabled={isActionDisabled}
            leftSection={isLastStep ? <IconCirclePlus /> : undefined}
            data-testid={isLastStep ? 'create-claim-submit' : undefined}
          >
            {isLastStep ? 'Create' : 'Next'}
          </Button>
        </Flex>
      )}
    </Stack>
  );
};

const TariffMultiSelect = ({
  value,
  providerID,
  siteID,
  enabled,
  onChange,
}: {
  value: string[];
  providerID?: string;
  siteID?: string;
  enabled: boolean;
  onChange: (value: string[]) => void;
}) => {
  const {
    data: providerTariffs = [],
    isLoading,
    isFetching,
  } = useListTariffsByProvider({
    providerID,
    siteID,
  });
  const previousProviderID = React.useRef<string | undefined>(providerID);

  const options = React.useMemo(() => {
    return providerTariffs
      .map((tariff) => ({
        value: tariff.id,
        label: `${tariff.prefix}${tariff.code} - ${tariff.description}`,
        code: `${tariff.prefix}${tariff.code}`,
        description: tariff.description,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true })
      );
  }, [providerTariffs]);

  useEffect(() => {
    if (!providerID && value.length) {
      onChange([]);
    }
  }, [providerID, value.length, onChange]);

  useEffect(() => {
    if (
      previousProviderID.current &&
      providerID &&
      previousProviderID.current !== providerID
    ) {
      onChange([]);
    }
    previousProviderID.current = providerID;
  }, [providerID, onChange]);

  const renderTariffOption = ({ option, checked }: any) => {
    if (!option) {
      return (
        <Text size="sm" c="dimmed">
          No tariff available
        </Text>
      );
    }

    return (
      <Group gap="sm" wrap="nowrap">
        <Text size="md" fw={checked ? 800 : 400}>
          {option.code ?? option.label}
        </Text>

        <Stack gap={2}>
          <Text size="sm" fw={200}>
            {option.description || 'No description'}
          </Text>
        </Stack>
      </Group>
    );
  };

  return (
    <MultiSelect
      label="Tariffs"
      placeholder={
        providerID ? 'Select tariffs' : 'Select a provider to load tariffs'
      }
      data={options}
      withCheckIcon
      value={value}
      searchable
      filter={filterTariffOptions}
      onChange={onChange}
      disabled={!providerID || !enabled}
      renderOption={renderTariffOption}
      rightSection={
        isLoading || isFetching ? (
          <Loader size={18} style={{ marginRight: 8 }} />
        ) : null
      }
      radius="lg"
      w="100%"
    />
  );
};
